import { useAuth } from '@saltus/auth';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import getSignature from '../actions/actioncentersignature/get';
import setSignatureForContactAction from '../actions/actioncentersignature/set';
import getApprovalDocAction from '../actions/approvaldocs/getApprovalDocAction';
import guestGetAssignedApprovalDocAction from '../actions/approvaldocs/guestGetAssignedApprovalDoc';
import setSignedApprovalDocAction from '../actions/approvaldocs/setSignedApprovalDoc';
import ActionSummary from './action-centre-action-summary';
import { PortalLayout } from '../components/layout/component';
import Loading from '../components/loading/component';
import {
  DeclineInvitationMutation,
  GetActionApprovalDocQuery,
  GetActiveLinkQuery,
  GetInvitationQuery,
  GetUnsignedApprovalDocDownloadDetailsQuery,
} from '../graphql/API';
import { getActiveLink, getInvitation } from '../graphql/queries';
import useQuery from '../hooks/useQuery';
import invariant from '../utils/invariant';
import { MapGetter, State } from '../utils/map';
import Questionnaire from './atr/questionnaire';
import Results from './atr/results';
import { AtrState } from '../reducers/atr/reducer';
import { TickGreenCircleIcon } from '../theme/styled-components';
import Button from '../ui/Button';
import Form from '../ui/Form';
import Link from '../ui/Link';
import { Wrapper, Title } from '../ui/Page';
import Panel from '../ui/Panel';
import Typography from '../ui/Typography';
import useMutation from '../hooks/useMutation';
import { declineInvitation } from '../graphql/mutations';

type ApprovalDoc = GetActionApprovalDocQuery['getActionApprovalDoc'];

type ApprovalDocDownload =
  GetUnsignedApprovalDocDownloadDetailsQuery['getUnsignedApprovalDocDownloadDetails'];

type AuthMode = 'iam' | 'userPool';

interface Document {
  DocFileName?: string;
}

interface DocsSelector {
  items: ApprovalDoc[];
}

interface ActionApprovalProps {
  authMode: AuthMode;
  contactId: string;
  resourceId: string;
}

interface AnnualReviewProps {
  contactId: string;
  resourceId: string;
}

interface AtrProps {
  contactId: string;
  resourceId: string;
}

const useStyles = makeStyles((theme) => ({
  buttonBar: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 24,
    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  strong: {
    fontWeight: 'bold',
  },
}));

function ActionApproval({
  authMode,
  contactId,
  resourceId,
}: ActionApprovalProps) {
  const navigate = useNavigate();

  const location = useLocation();

  const dispatch = useDispatch();

  const docs = useSelector<State<DocsSelector>, MapGetter<DocsSelector>>(
    (state) => state.approvaldocs.get('docs'),
  );

  const [item, setItem] = useState<ApprovalDoc>();

  const unsignedApprovalDoc = useSelector<
    State<ApprovalDocDownload>,
    MapGetter<ApprovalDocDownload>
  >((state) => state.approvaldocs.get('unsignedApprovalDoc'));

  const handleGetAssignedApprovalDoc = (document: Document) => {
    dispatch(
      guestGetAssignedApprovalDocAction(
        resourceId,
        contactId,
        authMode,
        document,
      ),
    );
  };

  const onSignatureData = (signature: string) => {
    dispatch(
      setSignatureForContactAction(signature, contactId, resourceId, authMode),
    );
  };

  const handleSignedApprovalDocAction = () => {
    dispatch(setSignedApprovalDocAction(resourceId, contactId, authMode));
  };

  const handleGetSignature = () => {
    dispatch(getSignature(contactId, resourceId, authMode));
  };

  useEffect(() => {
    dispatch(getApprovalDocAction(contactId, resourceId, authMode));
  }, [authMode, contactId, dispatch, resourceId]);

  useEffect(() => {
    if (docs?.items && docs.items.length > 0) {
      const [doc] = docs.items;

      setItem(doc);
    }
  }, [docs]);

  useEffect(() => {
    // Redirect to self while updating location.state.
    navigate(location.pathname, {
      state: {
        item,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  if (docs && item) {
    return (
      <ActionSummary
        approvalDocs={docs}
        unsignedApprovalDoc={unsignedApprovalDoc}
        handleGetAssignedApprovalDoc={handleGetAssignedApprovalDoc}
        onSignatureData={onSignatureData}
        handleSignedApprovalDocAction={handleSignedApprovalDocAction}
        handleGetSignature={handleGetSignature}
        showBackToAction={false}
      />
    );
  }

  return null;
}

function AnnualReview({ contactId, resourceId }: AnnualReviewProps) {
  const [complete, setComplete] = useState(false);

  const classes = useStyles();

  const { data, loading } = useQuery<GetInvitationQuery>(getInvitation, {
    authMode: 'iam',
    variables: {
      id: resourceId,
    },
  });

  const [decline, { loading: declineLoading }] =
    useMutation<DeclineInvitationMutation>(declineInvitation, {
      onCompleted: () => {
        setComplete(true);
      },
    });

  const handler = async () => {
    await decline({
      authMode: 'iam',
      variables: {
        id: resourceId,
      },
    });
  };

  if (loading || declineLoading) {
    return <Loading />;
  }

  return (
    <Wrapper>
      <Title>Your annual review</Title>

      {complete ? (
        <Panel centre>
          <TickGreenCircleIcon width={90} height={90} />

          <Typography align="center">
            Annual review successfully declined
          </Typography>
        </Panel>
      ) : (
        <Panel>
          <Typography variant="h2">
            You have indicated that you wish to decline the invitation for an
            annual review.
          </Typography>

          <Typography>
            To provide you with the most relevant advice it is important that
            Saltus know your current circumstances and your financial goals.
            <br />
            <span className={classes.strong}>
              We strongly recommend that you book an annual review meeting
            </span>
            , even if you are happy with your current strategy.
          </Typography>

          <Typography>
            Should you wish to decline this invitation, you must agree to the
            following statements.
          </Typography>

          <Form
            schema={{
              properties: {
                advice: {
                  title:
                    'I want to continue to recieve ongoing financial planning.',
                  type: 'boolean',
                },
                circumstances: {
                  title:
                    'I confirm there have been no updates to my personal circumstances.',
                  type: 'boolean',
                },
                decline: {
                  title:
                    'I wish to decline this annual review. I understand that this will close the invitation, and that I will next be contacted in 12 months time.',
                  type: 'boolean',
                },
                profile: {
                  title: 'I am happy with my current risk profile.',
                  type: 'boolean',
                },
              },
              required: ['advice', 'circumstances', 'decline', 'profile'],
              type: 'object',
            }}
            ui={{
              'ui:order': ['circumstances', 'profile', 'advice', 'decline'],
            }}
            onSubmit={handler}
          >
            <div className={classes.buttonBar}>
              <Button type="submit">Decline invitation</Button>

              {data?.getInvitation && (
                <Typography>
                  Alternatively if you&apos;ve reconsidered, you can{' '}
                  <Link href={data.getInvitation.link}>
                    book an annual review meeting
                  </Link>{' '}
                  instead.
                </Typography>
              )}
            </div>
          </Form>
        </Panel>
      )}
    </Wrapper>
  );
}

function Atr({ contactId, resourceId }: AtrProps) {
  const { score } = useSelector<
    {
      atr: AtrState;
    },
    AtrState
  >((state) => state.atr);

  return score > 0 ? (
    <Results score={score} />
  ) : (
    <Questionnaire contactId={contactId} resourceId={resourceId} />
  );
}

function Landing() {
  const { id, resourceId, resourceType } = useParams();

  const { user, loading: userLoading } = useAuth();

  const authMode = user ? 'userPool' : 'iam';

  invariant(resourceId);

  const {
    data,
    error,
    loading: dataLoading,
  } = useQuery<GetActiveLinkQuery>(
    getActiveLink,
    {
      authMode,
      variables: {
        id,
        resourceId,
        resourceType,
      },
    },
    !userLoading,
  );

  if (dataLoading || userLoading) {
    return <Loading />;
  }

  if (error) {
    return <Navigate to="/" />;
  }

  if (data?.getActiveLink?.contactId) {
    return (
      <PortalLayout appBar={false} automaticLogout={false}>
        <div data-testid="landing-content">
          {(() => {
            switch (resourceType) {
              case 'action-approval':
                return (
                  <ActionApproval
                    authMode={authMode}
                    contactId={data.getActiveLink.contactId}
                    resourceId={resourceId}
                  />
                );
              case 'annual-review':
                return (
                  <AnnualReview
                    contactId={data.getActiveLink.contactId}
                    resourceId={resourceId}
                  />
                );
              case 'Atr':
                return (
                  <Atr
                    contactId={data.getActiveLink.contactId}
                    resourceId={resourceId}
                  />
                );
              default:
                return <Navigate to="/" />;
            }
          })()}
        </div>
      </PortalLayout>
    );
  }

  return null;
}

export default Landing;
